/**
 * Support message API helper for the /help page.
 *
 * WHAT THIS FILE DOES:
 * - Provides a tiny client wrapper around /api/support-messages endpoints.
 * - Handles direct-to-R2 attachment uploads via presigned URLs.
 * - Normalizes server error codes into human-friendly copy.
 *
 * WHY THIS EXISTS:
 * - Keeps network + upload logic out of the UI controller.
 * - Makes the help UI easier to reason about and test.
 * - Reuses the same backend flow as the extension options modal.
 *
 * ARCHITECTURE CONTEXT:
 * - /help is a public SSR page that mounts a small client bundle.
 * - Firebase Auth provides an ID token for Authorization.
 * - Backend routes live at /api/support-messages (see routes/support-messages.ts).
 * - Attachments are uploaded directly to R2 via presigned URLs.
 *
 * DATA FLOW:
 * 1) Request upload URLs (JSON) for attachments.
 * 2) PUT each attachment to its presigned URL.
 * 3) POST multipart form with text/audio + attachment metadata.
 * 4) Backend stores metadata + schedules webhook delivery.
 *
 * ERROR HANDLING:
 * - API errors return { error: string } codes; we map to user copy.
 * - 5xx on upload-urls triggers fallback to inline attachment uploads.
 * - All other failures surface as UploadErrors.FAILED.
 *
 * SECURITY NOTES:
 * - Authorization uses Firebase ID token in Bearer format.
 * - Direct upload URLs are short-lived and scoped to a single object.
 * - The help-contact page never stores tokens or media beyond in-memory state.
 *
 * RELATED FILES:
 * - packages/backend/src/client/help-contact.ts (UI controller)
 * - packages/backend/src/api/routes/support-messages.ts (API endpoints)
 * - packages/chrome-plugin/extension/ui/options/support-message-modal.js (reference flow)
 * - packages/backend/src/api/routes/support-messages/handlers.ts (validation + storage)
 *
 * NON-GOALS:
 * - No retry/backoff logic (keep UX simple; user can re-send).
 * - No streaming progress UI (large uploads handled by R2).
 *
 * FILE HEADER SIZE TARGET:
 * - Keep this header between 50-120 lines for LLM readability.
 */

const SupportMessageEndpoints = {
  SUBMIT: '/api/support-messages',
  ATTACHMENT_UPLOAD_URLS: '/api/support-messages/attachments/upload-urls'
} as const;

const SupportMessageErrorMessages: Record<string, string> = {
  text_audio_or_attachments_required: 'Please add text, a voice note, or attachments.',
  text_too_long: 'Your message is too long. Please keep it under 50,000 characters.',
  audio_too_large: 'Voice note is too large. Please keep it under 25 MB.',
  audio_empty: 'Voice note is empty. Please re-record.',
  audio_type_unsupported: 'Voice note format is not supported.',
  audio_duration_required: 'Voice note duration is missing. Please re-record.',
  audio_duration_too_long: 'Voice note is too long. Max 10 minutes.',
  attachment_too_large: 'Attachment is too large. Max 2 GB per file.',
  attachment_empty: 'Attachment is empty. Please re-select.',
  attachment_type_unsupported: 'Only JPEG, PNG, WebP, PDF, or supported video files are allowed.',
  attachment_executable: 'Executable files are not allowed.',
  attachment_not_found: 'Attachment upload failed. Please re-attach and try again.',
  attachment_key_invalid: 'Attachment upload failed. Please re-attach and try again.',
  attachment_upload_request_invalid: 'Attachment upload failed. Please re-attach and try again.',
  direct_upload_unavailable: 'Attachment uploads are temporarily unavailable.',
  support_message_id_required: 'Attachment upload expired. Please re-attach and try again.',
  support_message_id_invalid: 'Attachment upload expired. Please re-attach and try again.',
  support_message_id_taken: 'Attachment upload expired. Please re-attach and try again.'
};

export const UploadErrors = {
  UNAUTHENTICATED: 'Please sign in to send a support message.',
  FAILED: 'Upload failed. Please check your connection and try again.'
} as const;

export interface AttachmentUploadMeta {
  id: string;
  key: string;
  mimeType: string | null;
  sizeBytes: number;
  originalName: string | null;
}

export interface SupportMessageSubmission {
  /** Firebase ID token or extension worker session token (Bearer). */
  bearerToken: string;
  text: string;
  audioBlob: Blob | null;
  audioFileName: string | null;
  audioDurationMs: number | null;
  attachments: File[];
}

interface AttachmentUploadResult {
  supportMessageId: string | null;
  attachmentUploads: AttachmentUploadMeta[];
  fallbackToForm: boolean;
}

interface SupportMessageErrorPayload {
  error?: string;
}

interface AttachmentUploadUrlPayload {
  supportMessageId?: string;
  uploads?: Array<{
    uploadUrl?: string;
    key?: string;
    attachmentId?: string;
    contentType?: string;
    mimeType?: string;
    sizeBytes?: number;
    originalName?: string;
  }>;
}

/**
 * Build headers for support message API calls.
 *
 * @param bearerToken - Firebase ID token or worker session token for Authorization.
 * @param extra - Optional additional headers.
 * @returns Headers object with Authorization + Accept set.
 */
function buildAuthHeaders(bearerToken: string, extra?: HeadersInit): Headers {
  const headers = new Headers(extra ?? {});
  headers.set('Authorization', `Bearer ${bearerToken}`);
  if (!headers.has('Accept')) {
    headers.set('Accept', 'application/json');
  }
  return headers;
}

/**
 * Read a user-facing error message from a support message response.
 *
 * @param response - Backend response.
 * @returns Human-friendly error message string.
 */
async function readSupportMessageError(response: Response): Promise<string> {
  try {
    const payload = await response.json() as SupportMessageErrorPayload;
    if (typeof payload.error === 'string') {
      return SupportMessageErrorMessages[payload.error] ?? payload.error;
    }
  } catch {
    // ignore JSON parse failures
  }
  return UploadErrors.FAILED;
}

/**
 * Determine whether to fall back to inline attachment uploads.
 *
 * @param response - Response from the upload-urls endpoint.
 * @returns True when inline uploads should be attempted.
 */
async function shouldFallbackToInlineAttachments(response: Response): Promise<boolean> {
  if (response.status >= 500) {
    return true;
  }

  try {
    const payload = await response.json() as SupportMessageErrorPayload;
    return payload.error === 'direct_upload_unavailable';
  } catch {
    return false;
  }
}

/**
 * Upload attachments directly to R2 using presigned URLs.
 *
 * @param bearerToken - Firebase ID token or worker session token.
 * @param attachments - Attachment files selected by the user.
 * @returns Upload metadata and support message ID.
 */
async function uploadAttachmentsToR2(
  bearerToken: string,
  attachments: File[]
): Promise<AttachmentUploadResult> {
  const uploadRequest = {
    attachments: attachments.map((file) => ({
      name: typeof file.name === 'string' ? file.name : '',
      mimeType: typeof file.type === 'string' ? file.type : '',
      sizeBytes: typeof file.size === 'number' ? file.size : 0
    }))
  };

  const uploadUrlResponse = await fetch(
    SupportMessageEndpoints.ATTACHMENT_UPLOAD_URLS,
    {
      method: 'POST',
      headers: buildAuthHeaders(bearerToken, { 'Content-Type': 'application/json' }),
      body: JSON.stringify(uploadRequest)
    }
  );

  if (!uploadUrlResponse.ok) {
    const shouldFallback = await shouldFallbackToInlineAttachments(uploadUrlResponse.clone());
    if (shouldFallback) {
      return { supportMessageId: null, attachmentUploads: [], fallbackToForm: true };
    }
    throw new Error(await readSupportMessageError(uploadUrlResponse));
  }

  const payload = await uploadUrlResponse.json() as AttachmentUploadUrlPayload;
  const supportMessageId = typeof payload.supportMessageId === 'string' ? payload.supportMessageId : null;
  const uploads = Array.isArray(payload.uploads) ? payload.uploads : [];

  if (!supportMessageId || uploads.length !== attachments.length) {
    throw new Error(UploadErrors.FAILED);
  }

  const attachmentUploads: AttachmentUploadMeta[] = [];

  for (let index = 0; index < uploads.length; index += 1) {
    const upload = uploads[index] ?? {};
    const file = attachments[index];
    const uploadUrl = typeof upload.uploadUrl === 'string' ? upload.uploadUrl : '';
    const uploadKey = typeof upload.key === 'string' ? upload.key : '';
    const attachmentId = typeof upload.attachmentId === 'string' ? upload.attachmentId : '';
    const contentType = typeof upload.contentType === 'string' && upload.contentType
      ? upload.contentType
      : (file.type || 'application/octet-stream');

    if (!uploadUrl || !uploadKey || !attachmentId) {
      throw new Error(UploadErrors.FAILED);
    }

    const uploadResponse = await fetch(uploadUrl, {
      method: 'PUT',
      headers: { 'Content-Type': contentType },
      body: file
    });

    if (!uploadResponse.ok) {
      throw new Error(UploadErrors.FAILED);
    }

    attachmentUploads.push({
      id: attachmentId,
      key: uploadKey,
      mimeType: typeof upload.mimeType === 'string' ? upload.mimeType : null,
      sizeBytes: typeof upload.sizeBytes === 'number' ? upload.sizeBytes : file.size,
      originalName: typeof upload.originalName === 'string' ? upload.originalName : (file.name || null)
    });
  }

  return { supportMessageId, attachmentUploads, fallbackToForm: false };
}

/**
 * Submit a support message via the backend API.
 *
 * @param payload - Support message submission payload.
 */
export async function submitSupportMessage(payload: SupportMessageSubmission): Promise<void> {
  if (!payload.bearerToken) {
    throw new Error(UploadErrors.UNAUTHENTICATED);
  }

  const formData = new FormData();
  if (payload.text) {
    formData.append('text', payload.text);
  } else {
    formData.append('text', '');
  }

  if (payload.audioBlob) {
    formData.append('audio', payload.audioBlob, payload.audioFileName ?? 'voice-note.webm');
    if (payload.audioDurationMs !== null) {
      formData.append('audioDurationMs', String(payload.audioDurationMs));
    }
  }

  if (payload.attachments.length > 0) {
    const uploadResult = await uploadAttachmentsToR2(payload.bearerToken, payload.attachments);
    if (uploadResult.fallbackToForm) {
      payload.attachments.forEach((file) => {
        formData.append('attachments', file, file.name || 'attachment');
      });
    } else {
      if (!uploadResult.supportMessageId) {
        throw new Error(UploadErrors.FAILED);
      }
      formData.append('supportMessageId', uploadResult.supportMessageId);
      if (uploadResult.attachmentUploads.length > 0) {
        formData.append('attachmentsMeta', JSON.stringify(uploadResult.attachmentUploads));
      }
    }
  }

  const response = await fetch(
    SupportMessageEndpoints.SUBMIT,
    {
      method: 'POST',
      headers: buildAuthHeaders(payload.bearerToken),
      body: formData
    }
  );

  if (!response.ok) {
    throw new Error(await readSupportMessageError(response));
  }
}
